import React from 'react';
import { Background, Node as _ReactFlowNode } from 'reactflow';
import { twJoin } from 'tailwind-merge';

import { Icon, RyeconSpinner } from '@dbt-labs/sourdough';

import { LOADING_STATES, QueryStatus } from '../dag/DagInterfaces';
import { IdleNodeDataType } from '../dag/internalDag';
import { COLORS } from '../dag/styles';

export type IdleReactFlowNode = _ReactFlowNode<IdleNodeDataType> & {
  data: IdleNodeDataType;
};
export interface DagContainerDimensions {
  width: number;
  height: number;
}

export interface IdleDagProps {
  status?: QueryStatus;
  showLoadingScreen?: boolean;
  errorMessages?: string[];
  children?: any;
}

export const IdleDag: React.FC<React.PropsWithChildren<IdleDagProps>> = ({
  status,
  showLoadingScreen,
  errorMessages,
  children,
}) => {
  const [, setDagContainerEl] = React.useState<HTMLDivElement | null>();

  return (
    <div className="h-full w-full bg-bgBackground">
      <div
        ref={(newRef) => setDagContainerEl(newRef)}
        className="relative h-full w-full"
      >
        <Background color={COLORS.BG_DOTS} gap={12} size={1} />
      </div>
      <div className="align-center absolute inset-x-0 inset-y-1/2 inline h-fit">
        <IdleDagText
          status={status}
          showLoadingScreen={showLoadingScreen}
          errorMessages={errorMessages}
          children={children}
        />
      </div>
    </div>
  );
};

const IdleDagText: React.FC<IdleDagProps> = ({
  status,
  showLoadingScreen,
  errorMessages,
  children,
}) => {
  const ellipsesColor = 'dark:loading-dark loading';
  // If `showLoadingScreen` is undefined, it's ignored
  if (
    (showLoadingScreen !== false && status && LOADING_STATES.includes(status)) ||
    showLoadingScreen
  ) {
    return (
      <>
        <div className={`text-md absolute top-2 w-full -translate-y-1/2 text-center`}>
          <Icon
            ryecon={RyeconSpinner}
            className={`motion-safe:animate-spin-slow ${ellipsesColor} `}
            size="xl"
          />
          <h2 className={twJoin('mb-2 text-base font-normal', 'text-fgMain')}>
            Lineage is Loading...
          </h2>
          <ol className={twJoin('text-sm leading-6', 'text-fgAlt')}>
            <li>This might take a minute, depending on the size of your DAG.</li>
          </ol>
        </div>
      </>
    );
  }
  // Default error message in case no other messages are available or status is 'error'.
  let displayMessages: string[] = [
    'Lineage is currently unavailable.',
    'Check that your project does not contain compilation errors or contact support if this error persists.',
  ];
  if (errorMessages?.length) {
    displayMessages = errorMessages;
  }
  const newErrorList: React.ReactElement[] = [];
  Object.values(displayMessages).forEach((message, keyNum) => {
    if (keyNum === 0) {
      return;
    }
    newErrorList.push(<li key={keyNum}>{message}</li>);
  });

  return (
    <div className={`text-md absolute top-2 w-full -translate-y-1/2 text-center`}>
      <h2 className={twJoin('mb-2 text-base font-normal', 'text-fgMain')}>
        {displayMessages[0]}
      </h2>
      <ol className={twJoin('text-sm leading-6', 'text-fgAlt')}>{newErrorList}</ol>
      {children}
    </div>
  );
};
